import React from 'react';
import debounce from 'lodash/debounce';
import { useLazyGetTopCitiesResultQuery } from 'store/api/activitiesApi';
import { useSelector } from 'react-redux';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { selectPageTypeId } from 'store/slice/navigation';
import { Box, HorizontalScroll, SkeletonLoader, Typography } from '@drone-js/core';
import { TopCitiesResponse } from 'store/api/api.types';
import { getTopCities } from 'component/common/TopCitiesCarousel/TopCitiesCarouselUtils';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { TopCitiesItem } from 'component/common/TopCitiesCarousel/TopCitiesItem';
import { TopCitiesCarouselTypes } from 'component/common/TopCitiesCarousel/TopCitiesCarousel.types';
import { selectScreenSize } from 'store/slice/screen';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesFieldsV1,
    ActivitiesTopDestinationContainerSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesManifestV1,
    ActivitiesTopDestinationContainerSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import useScrollDirection, { ScrollDirection } from 'core/utils/useScrollDirection';

const TopCitiesCarouselPlaceholder: React.FC<{ isCompact?: boolean; cms: CmsValue }> = ({ isCompact, cms }) => {
    const loaderLabel = cms[326495];
    return (
        <>
            <SkeletonLoader label={loaderLabel} shape="rectangle" width="350px" height="20px" me="12" mb="4" />
            <HorizontalScroll data-testid="activities-top-cities-carousel" py="4" px="2">
                {[...Array<number>(10)].map((_, i) => (
                    <div key={i}>
                        <SkeletonLoader label={loaderLabel} shape="rectangle" width={isCompact ? '124px' : '200px'} height={isCompact ? '156px' : '240px'} me="12" />
                    </div>
                ))}
            </HorizontalScroll>
        </>
    );
};
TopCitiesCarouselPlaceholder.displayName = 'TopCitiesCarouselPlaceholder';

export const TopCitiesCarousel: React.FC<TopCitiesCarouselTypes> = (props) => {
    const [triggerGetTopCitiesResult, topCitiesResponse] = useLazyGetTopCitiesResultQuery<TopCitiesResponse>();
    const config = useSelector(selectConfigFromBootstrap);
    const cms = config.cms;
    const pageTypeId = useSelector(selectPageTypeId);
    const screenSize = useSelector(selectScreenSize);
    const isPageContainerSpacing = useSelector(selectFeatureBehaviorFromBootstrap).PageContainerSpacing;
    const isMobileScreen = screenSize.mobile;
    const isCompact = props.variant === 'compact';

    const ptDefault = isMobileScreen ? '16' : '48';
    const pt = isPageContainerSpacing ? '16' : isCompact ? undefined : ptDefault;
    const borderRadius = isCompact ? '16' : undefined;
    const minHeight = isCompact ? undefined : '380px';
    const title = props.title || cms[298697];
    const { handleScroll } = useScrollDirection();

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        triggerGetTopCitiesResult({
            isMobileScreen,
            isHomePage: isCompact,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const topCities = React.useMemo(() => getTopCities(topCitiesResponse, pageTypeId, config), [topCitiesResponse, pageTypeId, config]);

    React.useEffect(() => {
        topCities.length > 0 &&
            trackElementEvent<ActivitiesTopDestinationContainerSeenEventActivitiesFieldsV1>(ActivitiesTopDestinationContainerSeenEventActivitiesManifestV1.ids, {});
    }, [topCities]);

    const trackScrollEvent = React.useCallback((scrollDirection: ScrollDirection) => {
        trackElementEvent<ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesFieldsV1>(
            ActivitiesTopDestinationContainerArrowButtonClickEventActivitiesManifestV1.ids,
            {
                action_element_value: scrollDirection,
            },
        );
    }, []);

    const onHorizontalScroll = React.useMemo(
        () =>
            debounce((event: React.SyntheticEvent<HTMLElement, Event>) => {
                const scrollDirection = handleScroll(event);
                scrollDirection && trackScrollEvent(scrollDirection);
            }, 300),
        [handleScroll, trackScrollEvent],
    );

    if (!topCitiesResponse.isFetching && topCities.length === 0) {
        return null;
    }

    const renderCarousel = () => (
        <HorizontalScroll
            nextLabel={config.cms[130706]}
            previousLabel={config.cms[130765]}
            onScroll={onHorizontalScroll}
            p="16"
            px="2"
            py="2"
            data-testid="activities-top-cities-carousel"
            data-element-value="activities-top-cities-horizontal-scroll"
        >
            {topCities.map((city, index) => (
                <Box ms={index === 0 ? '2' : '0'} key={city.cityId}>
                    <TopCitiesItem city={city} cid={config.pageConfig.cid} isCompact={isCompact} index={index} />
                </Box>
            ))}
        </HorizontalScroll>
    );
    const renderContent = () => (
        <>
            {topCitiesResponse.isFetching ? (
                <TopCitiesCarouselPlaceholder cms={cms} isCompact={isCompact} />
            ) : (
                <>
                    <Typography variant="heading-default" pb="16">
                        {title}
                    </Typography>
                    {renderCarousel()}
                </>
            )}
        </>
    );

    return (
        <Box
            backgroundColor="base.surface"
            pt={pt}
            borderRadius={borderRadius}
            data-testid="activities-top-cities-carousel-container"
            data-element-name="activities-top-cities-carousel-container"
            minHeight={minHeight}
        >
            <>
                {!isCompact || !isPageContainerSpacing ? (
                    <ContentContainer my="auto" enforceDefaultSpacing={true} isFullContentWidth={true}>
                        {renderContent()}
                    </ContentContainer>
                ) : (
                    renderContent()
                )}
            </>
        </Box>
    );
};

TopCitiesCarousel.displayName = 'TopCitiesCarousel';
